/**
 * LeaderboardScene
 *
 * Displays global leaderboard with top players
 */

import Phaser from 'phaser';
import { GAME_CONSTANTS } from '../config/constants';
import { ICONS } from '../config/icons';
import { UsernameManager } from '../systems/UsernameManager';
import { LeaderboardManager, CombinedLeaderboardEntry } from '../systems/LeaderboardManager';
import { VictoryHistoryManager } from '../systems/VictoryHistoryManager';
import { BestHeightManager } from '../systems/BestHeightManager';
import { GlassButton } from '../ui/components/GlassButton';
import { Z_INDEX } from '../config/designSystem';
import { sanitizeUsername } from '../utils/security';

export class LeaderboardScene extends Phaser.Scene {
  private combinedLeaderboardData: CombinedLeaderboardEntry[] = [];
  private currentUsername: string = '';
  private isTransitioning: boolean = false;
  private isLoading: boolean = false;
  private loadError: boolean = false;
  private isOffline: boolean = false;
  private loadingSpinner?: Phaser.GameObjects.Graphics;
  private loadingText?: Phaser.GameObjects.Text;

  // Scrolling state
  private isDragging: boolean = false;
  private dragStartY: number = 0;
  private scrollStartY: number = 0;

  constructor() {
    super({ key: 'LeaderboardScene' });
  }

  async create(): Promise<void> {
    const { width, height } = this.cameras.main;

    // Reset state
    this.isTransitioning = false;
    this.isLoading = false;
    this.loadError = false;

    // Get current username
    this.currentUsername = UsernameManager.getUsername();

    // Create background
    this.createBackground(width, height);

    // Display title
    this.displayTitle(width, height);

    // Fetch and display global leaderboard
    await this.fetchGlobalLeaderboard();
    const contentHeight = this.displayGlobalLeaderboard(width, height);

    // Create buttons
    this.createButtons(width, height);

    // Enable scrolling if content exceeds screen height
    // Add 100px bottom padding for comfortable scrolling
    const totalHeight = Math.max(height, contentHeight + 100);
    this.cameras.main.setBounds(0, 0, width, totalHeight);
    this.cameras.main.setScroll(0, 0);

    // Setup touch/mouse scrolling
    this.setupScrolling(height, totalHeight);
  }

  /**
   * Setup scrolling behavior for leaderboard
   */
  private setupScrolling(height: number, totalHeight: number): void {
    // Only enable scrolling if content is larger than screen
    if (totalHeight <= height) {
      return;
    }

    // Make entire screen draggable
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.isDragging = true;
      this.dragStartY = pointer.y;
      this.scrollStartY = this.cameras.main.scrollY;
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.isDragging && pointer.isDown) {
        const deltaY = this.dragStartY - pointer.y;
        const newScrollY = this.scrollStartY + deltaY;

        // Clamp scroll to valid range
        const maxScroll = totalHeight - height;
        this.cameras.main.scrollY = Phaser.Math.Clamp(newScrollY, 0, maxScroll);
      }
    });

    this.input.on('pointerup', () => {
      this.isDragging = false;
    });
  }

  private createBackground(width: number, height: number): void {
    const bg = this.add.graphics();
    bg.fillStyle(0x0a0e27, 0.95);
    bg.fillRect(0, 0, width, height);
    bg.setScrollFactor(0);
  }

  private displayTitle(width: number, height: number): void {
    // Responsive font size
    const isMobile = width < 768;
    const titleSize = isMobile ? 36 : 48;
    const iconSize = isMobile ? 32 : 40;
    const titleY = height * 0.08;

    // Create title text (center)
    const title = this.add.text(width / 2, titleY, 'LEADERBOARD', {
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      fontSize: `${titleSize}px`,
      color: '#FFD700',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 6,
      resolution: Math.max(3, window.devicePixelRatio),
    });
    title.setOrigin(0.5);
    title.setScrollFactor(0);

    // Create trophy icons on both sides
    const spacing = 20;
    const leftIconX = width / 2 - title.width / 2 - iconSize / 2 - spacing;
    const rightIconX = width / 2 + title.width / 2 + iconSize / 2 + spacing;

    const leftIcon = this.add.image(leftIconX, titleY, ICONS.TROPHY);
    leftIcon.setDisplaySize(iconSize, iconSize);
    leftIcon.setTint(0xffd700); // Gold tint to match title
    leftIcon.setScrollFactor(0);

    const rightIcon = this.add.image(rightIconX, titleY, ICONS.TROPHY);
    rightIcon.setDisplaySize(iconSize, iconSize);
    rightIcon.setTint(0xffd700); // Gold tint to match title
    rightIcon.setScrollFactor(0);

    // Subtitle showing username (sanitized to prevent XSS)
    const subtitle = this.add.text(
      width / 2,
      height * 0.14,
      `Player: ${sanitizeUsername(this.currentUsername)}`,
      {
        fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
        fontSize: isMobile ? '18px' : '24px',
        color: '#ffffff',
        fontStyle: 'bold',
        resolution: Math.max(3, window.devicePixelRatio),
      }
    );
    subtitle.setOrigin(0.5);
    subtitle.setScrollFactor(0);
  }

  /**
   * Fetch combined leaderboard from API
   */
  private async fetchGlobalLeaderboard(): Promise<void> {
    this.isLoading = true;
    this.loadError = false;
    this.isOffline = false;

    try {
      console.log('📥 Fetching combined leaderboard...');

      // Fetch combined leaderboard (finishes first, then heights)
      const combined = await LeaderboardManager.getCombinedLeaderboard(50);

      if (combined && combined.length > 0) {
        this.combinedLeaderboardData = combined;
        const completed = combined.filter(e => e.type === 'completed').length;
        const heights = combined.filter(e => e.type === 'height').length;
        console.log(
          `✅ Loaded ${combined.length} entries (${completed} completed, ${heights} heights)`
        );

        // Debug: Log first completed entry to check collectibles
        const firstCompleted = combined.find(e => e.type === 'completed');
        if (firstCompleted) {
          console.log('🐞 First completed entry:', {
            username: firstCompleted.username,
            collectibles: firstCompleted.collectibles,
            type: firstCompleted.type,
          });
        }
      } else {
        this.combinedLeaderboardData = [];
        console.log('ℹ️ No leaderboard data available');
      }
    } catch (error) {
      console.error('❌ Failed to fetch leaderboard:', error);

      // Check if it's an offline error
      if (
        error instanceof Error &&
        (error.message === 'OFFLINE' || error.name === 'AbortError' || !navigator.onLine)
      ) {
        this.isOffline = true;
        console.log('🔌 Device is offline');
      } else {
        this.loadError = true;
      }
    } finally {
      this.isLoading = false;
      this.cleanupLoadingAnimation();
    }
  }

  /**
   * Display leaderboard
   * @returns The total content height
   */
  private displayGlobalLeaderboard(width: number, height: number): number {
    const isMobile = width < 768;
    const startY = height * 0.22;

    // Loading state with animated spinner
    if (this.isLoading) {
      this.createLoadingAnimation(width, height, isMobile);
      return height;
    }

    // Offline state
    if (this.isOffline) {
      this.createOfflineState(width, height, isMobile);
      return height;
    }

    // Error state (server error, not offline)
    if (this.loadError) {
      this.createErrorState(width, height, isMobile);
      return height;
    }

    // Empty state
    if (this.combinedLeaderboardData.length === 0) {
      const emptyText = this.add.text(
        width / 2,
        height / 2,
        'No entries yet.\n\nBe the first to play!',
        {
          fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
          fontSize: isMobile ? '14px' : '18px',
          color: GAME_CONSTANTS.UI.COLOR_SECONDARY,
          align: 'center',
          resolution: Math.max(3, window.devicePixelRatio),
        }
      );
      emptyText.setOrigin(0.5);
      emptyText.setScrollFactor(0);
      return height;
    }

    // === COMBINED LEADERBOARD (finishes first, then heights) ===
    const entryHeight = isMobile ? 30 : 35;
    let currentY = startY;

    // Header row with combined columns
    this.createCombinedHeaderRow(width, currentY, isMobile);
    currentY += isMobile ? 30 : 35;

    // Display up to 50 entries from combined leaderboard
    const maxEntries = isMobile ? 30 : 50; // Slightly fewer on mobile for performance
    const entriesToShow = Math.min(maxEntries, this.combinedLeaderboardData.length);

    for (let i = 0; i < entriesToShow; i++) {
      const entry = this.combinedLeaderboardData[i];
      if (!entry) continue;

      const isCurrentUser = entry.username === this.currentUsername;
      this.createCombinedEntryRow(width, currentY, entry, isCurrentUser, isMobile);
      currentY += entryHeight;
    }

    // Return the final Y position (total content height)
    return currentY;
  }

  private createCombinedHeaderRow(width: number, y: number, isMobile: boolean): void {
    const headerStyle = {
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      fontSize: isMobile ? '15px' : '18px',
      color: '#e0e0e0',
      fontStyle: 'bold',
      resolution: Math.max(3, window.devicePixelRatio),
    };

    // Rank (smaller column)
    this.add.text(width * 0.08, y, '#', headerStyle).setOrigin(0.5);

    // Player (left-aligned with more space for longer names)
    this.add.text(width * 0.15, y, 'Player', headerStyle).setOrigin(0, 0.5);

    // Time/Height (with line break to make narrower) - moved to the right
    this.add
      .text(width * 0.7, y, isMobile ? 'Time/\nHeight' : 'Time/Height', {
        ...headerStyle,
        align: 'center',
        lineSpacing: isMobile ? -4 : 0,
      })
      .setOrigin(0.5);

    // Collectibles (diamond icon)
    const collectiblesIcon = this.add.image(width * 0.88, y, ICONS.DIAMOND);
    const iconSize = isMobile ? 20 : 24;
    collectiblesIcon.setDisplaySize(iconSize, iconSize);
    collectiblesIcon.setTint(0xffd700); // Gold tint to match header style
  }

  private createCombinedEntryRow(
    width: number,
    y: number,
    entry: CombinedLeaderboardEntry,
    isCurrentUser: boolean,
    isMobile: boolean
  ): void {
    const textColor = isCurrentUser ? '#4ECDC4' : '#f0f0f0';
    const backgroundColor = isCurrentUser ? 0x4ecdc4 : 0x1a1a2a;

    // Background highlight
    const bg = this.add.graphics();
    if (isCurrentUser) {
      bg.fillStyle(backgroundColor, 0.2);
      const bgHeight = isMobile ? 28 : 32;
      bg.fillRoundedRect(width * 0.03, y - (bgHeight / 2 - 2), width * 0.94, bgHeight, 6);
    }

    const entryStyle = {
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      fontSize: isMobile ? '15px' : '18px',
      color: textColor,
      fontStyle: isCurrentUser ? 'bold' : 'normal',
      resolution: Math.max(3, window.devicePixelRatio),
    };

    // Rank (smaller column)
    this.add.text(width * 0.08, y, `${entry.rank}`, entryStyle).setOrigin(0.5);

    // Player name (with indicator for current user, left-aligned with more space)
    // Sanitize username to prevent XSS attacks
    const safeName = sanitizeUsername(entry.username);
    const displayName = isCurrentUser ? `► ${safeName}` : safeName;
    const maxNameLength = isMobile ? 21 : 27; // Increased for wider column after moving Time/Height to the right
    const truncatedName =
      displayName.length > maxNameLength
        ? displayName.substring(0, maxNameLength) + '...'
        : displayName;
    this.add.text(width * 0.15, y, truncatedName, entryStyle).setOrigin(0, 0.5);

    // Time or Height (depending on entry type)
    if (entry.type === 'completed') {
      // Show completion time
      this.add
        .text(width * 0.7, y, this.formatTime(entry.completionTime!), entryStyle)
        .setOrigin(0.5);

      // Show collectibles count (format: collected/total)
      const collectibles = entry.collectibles !== undefined ? entry.collectibles : 0;
      const total = entry.totalCollectibles || 45;
      const collectiblesText = `${collectibles}/${total}`;

      // Debug first entry
      if (entry.rank === 1) {
        console.log('🐞 Displaying rank 1 collectibles:', {
          username: entry.username,
          collectibles: entry.collectibles,
          totalCollectibles: entry.totalCollectibles,
          text: collectiblesText,
        });
      }

      this.add.text(width * 0.88, y, collectiblesText, entryStyle).setOrigin(0.5);
    } else {
      // Show best height
      this.add.text(width * 0.7, y, `${entry.bestHeight!.toFixed(1)}m`, entryStyle).setOrigin(0.5);

      // Show collectibles count for height entries too
      const collectibles = entry.collectibles !== undefined ? entry.collectibles : 0;
      const total = entry.totalCollectibles || 45;
      const collectiblesText = `${collectibles}/${total}`;
      this.add.text(width * 0.88, y, collectiblesText, entryStyle).setOrigin(0.5);
    }
  }

  /**
   * Create offline state with retry button
   */
  private createOfflineState(width: number, height: number, isMobile: boolean): void {
    const centerY = height / 2;

    // Offline message
    const offlineText = this.add.text(width / 2, centerY - 60, "🔌 You're Offline", {
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      fontSize: isMobile ? '24px' : '32px',
      color: '#FFA500',
      fontStyle: 'bold',
      align: 'center',
      resolution: Math.max(3, window.devicePixelRatio),
    });
    offlineText.setOrigin(0.5);
    offlineText.setScrollFactor(0);

    const infoText = this.add.text(
      width / 2,
      centerY - 10,
      "Can't load leaderboard.\nPlease check your internet connection.",
      {
        fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
        fontSize: isMobile ? '14px' : '16px',
        color: GAME_CONSTANTS.UI.COLOR_SECONDARY,
        align: 'center',
        resolution: Math.max(3, window.devicePixelRatio),
      }
    );
    infoText.setOrigin(0.5);
    infoText.setScrollFactor(0);

    // Retry button
    const retryButton = new GlassButton(this, width / 2, centerY + 60, {
      text: 'RETRY',
      iconKey: ICONS.REFRESH,
      fontSize: isMobile ? 16 : 20,
      width: isMobile ? 140 : 180,
      height: isMobile ? 48 : 56,
      style: 'primary',
      onClick: async () => {
        // Clear the scene and retry
        this.children.removeAll();
        await this.create();
      },
    });
    retryButton.setDepth(Z_INDEX.uiContent);
  }

  /**
   * Create error state with retry button
   */
  private createErrorState(width: number, height: number, isMobile: boolean): void {
    const centerY = height / 2;

    const errorText = this.add.text(width / 2, centerY - 60, '❌ Something Went Wrong', {
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      fontSize: isMobile ? '20px' : '28px',
      color: '#FF6B6B',
      fontStyle: 'bold',
      align: 'center',
      resolution: Math.max(3, window.devicePixelRatio),
    });
    errorText.setOrigin(0.5);
    errorText.setScrollFactor(0);

    const infoText = this.add.text(
      width / 2,
      centerY - 10,
      'Failed to load leaderboard.\nPlease try again later.',
      {
        fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
        fontSize: isMobile ? '14px' : '16px',
        color: GAME_CONSTANTS.UI.COLOR_SECONDARY,
        align: 'center',
        resolution: Math.max(3, window.devicePixelRatio),
      }
    );
    infoText.setOrigin(0.5);
    infoText.setScrollFactor(0);

    // Retry button
    const retryButton = new GlassButton(this, width / 2, centerY + 60, {
      text: 'RETRY',
      iconKey: ICONS.REFRESH,
      fontSize: isMobile ? 16 : 20,
      width: isMobile ? 140 : 180,
      height: isMobile ? 48 : 56,
      style: 'primary',
      onClick: async () => {
        // Clear the scene and retry
        this.children.removeAll();
        await this.create();
      },
    });
    retryButton.setDepth(Z_INDEX.uiContent);
  }

  /**
   * Create animated loading spinner
   */
  private createLoadingAnimation(width: number, height: number, isMobile: boolean): void {
    const centerX = width / 2;
    const centerY = height / 2;

    // Create spinning circle
    this.loadingSpinner = this.add.graphics();
    this.loadingSpinner.setScrollFactor(0);
    this.loadingSpinner.setDepth(1000);

    // Animate the spinner
    let rotation = 0;
    const spinnerRadius = isMobile ? 20 : 30;
    const lineWidth = isMobile ? 3 : 4;

    this.time.addEvent({
      delay: 50,
      callback: () => {
        if (!this.loadingSpinner || !this.isLoading) return;

        this.loadingSpinner.clear();
        this.loadingSpinner.lineStyle(lineWidth, 0x4ecdc4, 1);

        // Draw rotating arc
        const startAngle = Phaser.Math.DegToRad(rotation);
        const endAngle = Phaser.Math.DegToRad(rotation + 270);

        this.loadingSpinner.beginPath();
        this.loadingSpinner.arc(centerX, centerY - 30, spinnerRadius, startAngle, endAngle, false);
        this.loadingSpinner.strokePath();

        rotation += 10;
        if (rotation >= 360) rotation = 0;
      },
      loop: true,
    });

    // Loading text with animated dots
    this.loadingText = this.add.text(centerX, centerY + 20, 'Loading', {
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      fontSize: isMobile ? '16px' : '20px',
      color: GAME_CONSTANTS.UI.COLOR_PRIMARY,
      resolution: Math.max(3, window.devicePixelRatio),
    });
    this.loadingText.setOrigin(0.5);
    this.loadingText.setScrollFactor(0);
    this.loadingText.setDepth(1000);

    // Animate dots
    let dotCount = 0;
    this.time.addEvent({
      delay: 500,
      callback: () => {
        if (!this.loadingText || !this.isLoading) return;

        dotCount = (dotCount + 1) % 4;
        this.loadingText.setText('Loading' + '.'.repeat(dotCount));
      },
      loop: true,
    });
  }

  /**
   * Clean up loading animation
   */
  private cleanupLoadingAnimation(): void {
    if (this.loadingSpinner) {
      this.loadingSpinner.destroy();
      this.loadingSpinner = undefined;
    }
    if (this.loadingText) {
      this.loadingText.destroy();
      this.loadingText = undefined;
    }
  }

  private formatTime(milliseconds: number): string {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    const ms = Math.floor((milliseconds % 1000) / 100);
    return `${minutes}:${seconds.toString().padStart(2, '0')}.${ms}`;
  }

  private createButtons(width: number, height: number): void {
    const isMobile = width < 768;
    const buttonY = height * 0.9;
    const buttonFontSize = isMobile ? 16 : 20;
    const buttonWidth = isMobile ? 160 : 220;
    const buttonHeight = isMobile ? 48 : 56;

    // Adjust positioning for mobile to prevent overlap
    const leftButtonX = isMobile ? width * 0.28 : width * 0.35;
    const rightButtonX = isMobile ? width * 0.72 : width * 0.65;

    // Back to Menu button (centered left)
    const backButton = new GlassButton(this, leftButtonX, buttonY, {
      text: isMobile ? 'BACK TO\nMENU' : 'BACK TO MENU',
      iconKey: ICONS.ARROW_BACK,
      fontSize: buttonFontSize,
      width: buttonWidth,
      height: buttonHeight,
      style: 'secondary',
      onClick: () => {
        if (!this.isTransitioning) {
          this.returnToMenu();
        }
      },
    });
    backButton.setDepth(Z_INDEX.uiContent);
    backButton.setScrollFactor(0); // Keep button fixed at bottom

    // Change Name button (centered right)
    const changeNameButton = new GlassButton(this, rightButtonX, buttonY, {
      text: isMobile ? 'CHANGE\nNAME' : 'CHANGE NAME',
      iconKey: ICONS.EDIT,
      fontSize: buttonFontSize,
      width: buttonWidth,
      height: buttonHeight,
      style: 'secondary',
      onClick: () => {
        if (!this.isTransitioning) {
          this.promptChangeName();
        }
      },
    });
    changeNameButton.setDepth(Z_INDEX.uiContent);
    changeNameButton.setScrollFactor(0); // Keep button fixed at bottom
  }

  private returnToMenu(): void {
    this.isTransitioning = true;
    this.scene.start('MainMenuScene');
  }

  private async promptChangeName(): Promise<void> {
    // Use browser prompt for now (simple MVP)
    const newName = prompt(
      'Enter new username (2-20 characters, letters/numbers/dash/underscore only):',
      this.currentUsername
    );

    if (!newName) {
      return; // Cancelled
    }

    // Validate format first
    if (!UsernameManager.validateUsername(newName)) {
      alert(
        'Invalid username. Please use 2-20 characters (letters, numbers, dash, underscore only).'
      );
      return;
    }

    const oldUsername = this.currentUsername;

    // Show checking availability message
    const checkingText = this.add.text(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2,
      'Checking availability...',
      {
        fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
        fontSize: '20px',
        color: '#4ECDC4',
        backgroundColor: '#000000',
        padding: { x: 20, y: 10 },
        resolution: Math.max(3, window.devicePixelRatio),
      }
    );
    checkingText.setOrigin(0.5);
    checkingText.setDepth(10000);

    // Check if username is available
    const { available } = await UsernameManager.checkAvailability(newName, oldUsername);

    checkingText.destroy();

    if (!available) {
      alert(`Username "${newName}" is already taken. Please choose another name.`);
      return;
    }

    // Set the new username
    if (!UsernameManager.setUsername(newName)) {
      alert('Failed to update username. Please try again.');
      return;
    }

    // Show updating message
    const updatingText = this.add.text(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2,
      'Updating username...',
      {
        fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
        fontSize: '20px',
        color: '#FFD700',
        backgroundColor: '#000000',
        padding: { x: 20, y: 10 },
        resolution: Math.max(3, window.devicePixelRatio),
      }
    );
    updatingText.setOrigin(0.5);
    updatingText.setDepth(10000);

    try {
      // Update all global leaderboard entries with the new username
      const updateResult = await LeaderboardManager.updateUsername(oldUsername, newName);

      if (updateResult.success) {
        console.log(`✅ Updated ${updateResult.totalUpdated} global leaderboard entries`);
      } else {
        console.warn('⚠️ Global leaderboard update returned success: false');
      }

      // Update all local leaderboard entries with the new username
      VictoryHistoryManager.updateUsername(oldUsername, newName);
      BestHeightManager.updateUsername(oldUsername, newName);

      // Small delay to ensure API changes propagate
      await new Promise(resolve => setTimeout(resolve, 500));

      // Success - reload scene to show new name
      updatingText.destroy();
      this.scene.restart();
    } catch (error) {
      console.error('❌ Failed to update username:', error);
      updatingText.destroy();
      alert('Failed to update username. Please try again.');
    }
  }
}
